<template>
	<div class="order-confirmation-container">
		<!-- Map -->
		<div class="map-wrapper" v-if="showMap()">
			<BaseMap
				:zoom=12
				:storeLocation="orderMap.origin"
				:directions="resolveMapDirections()"
				:height="getMapHeight"/>
		</div>
		<div class="map-wrapper" v-if="!appLoaded">
			<BaseLoader />
		</div>
		<!-- Order Number with message -->
		<div v-if="fromCheckout" class="center-message vertical-margins">
			<p>Awesome!</p>
			<p>Order &num;{{displayOrderId}} placed</p>
		</div>
		<!-- Sign up for guests -->
		<div v-if="isGuestLogin" class="has-center-text vertical-margins">
			<p>Sign in to track your next order from any device:</p>
			<p><strong><a @click.prevent="handleSignUpLink">Sign In or Sign Up</a></strong></p>
		</div>
		<!-- Order Number with message (Reorder view) -->
		<div v-if="!fromCheckout" class="center-message vertical-margins">
			<p>Your Recent Order &num;{{displayOrderId}}</p>
		</div>
		<!-- Order details minimal (readable) -->
		<div class="has-center-text vertical-margins">
			<p class="margin-80"><strong>{{displayServiceTypeUsed()}}</strong> for <strong>{{displayPromiseTime()}}</strong></p>
			<p class="margin-80">{{useVerb}} <strong>{{getOrderDestination}}</strong></p>
		</div>
		<!-- Contact store -->
		<div class="has-center-text vertical-margins" v-if="hasPhone">
			Questions? Call us at {{displayStorePhone}}
		</div>
		<!-- Order Tracker -->
		<div class="tracker-header has-center-text">
			<BaseIcon
				icon="radar"
				:editStroke="false"
				:isCustom="true"
				size="lg"/>
			<p>{{trackerName}}</p>
		</div>
		<order-tracker-container v-if="fromCheckout" />
		<BaseDecorator v-if="fromCheckout" name="frills" class="reciept-background">
			<template>
				<!-- Order items -->
				<div class="order-items">
					<item-list />
				</div>
				<!-- Actions -->
					<!-- Payment method -->
				<!-- <info-item name="Payment Method" :value="displayPaymentMethod()"/> -->
					<!-- Reciept Details -->
				<BaseActions :actions="orderConfirmationActions()" />
				<transition name="fade-down" mode="out-in">
					<div v-if="showRecieptDetails">
						<receipt-info :totals="getTotals" />
					</div>
				</transition>
				<div class="back-to-store full-width has-center-text">
					<a @click="_navigateToHome">Back to home</a>
				</div>
			</template>
		</BaseDecorator>
	</div>
</template>

<script>
import { CART, ACCOUNT, ACCOUNT_CHECKOUT, LOCATION, MENU_ITEMS, RECENT_ORDERS, APP_STORE } from '@/stores/defs';
import { mapGetters, mapActions } from 'vuex';
import ContentModal from '@ContentModal';
	// import OrderConfirmation from '@Checkout/OrderConfirmation';
	import WarningModal from '@Checkout/WarningModal.vue';
	import orderMixin from '@Mixin/orderMixin';
	import timeMixin from '@Mixin/timeMixin';
	import navigationMixin from '@Mixin/navigationMixin';
	import accountMixin from '@Mixin/accountMixin';
	import headerMixin from '@Mixin/headerMixin';
	import OrderTrackerVue from '@Orders/OrderTrackerContainer.vue';
	import ReceiptInfoVue from '@Checkout/ReceiptInfo.vue';
	const ItemListVue = (resolve) => {
		require.ensure([], () => {
			resolve(require('./ItemList.vue'));
		});
	};
	const InfoItemVue = (resolve) => {
		require.ensure([], () => {
			resolve(require('./InfoItem.vue'));
		});
	};
	const OrderTrackerContainer = (resolve) => {
		require.ensure([], () => {
			resolve(require('@/components/orders/OrderTrackerContainer'));
		});
	};
	export default {
		mixins: [
			orderMixin,
			timeMixin,
			navigationMixin,
			accountMixin,
			headerMixin,
		],
		components: {
			'item-list': ItemListVue,
			'info-item': InfoItemVue,
			'order-tracker': OrderTrackerVue,
			'order-tracker-container': OrderTrackerContainer,
			'receipt-info': ReceiptInfoVue,
		},
		props: {
			fromCheckout: {
				type: Boolean,
				default: true,
			},
		},
		data() {
			return {
				hasPhone: true,
				showRecieptDetails: false,
				skipGuard: false,
				orderMap: {
					destination: null,
					origin: null
				},
			};
		},
		computed: {
			...mapGetters({
				items: CART.ITEMS,
				priceTotal: CART.PRICE,
				isGuest: ACCOUNT_CHECKOUT.GET_IS_GUEST,
				guestId: ACCOUNT_CHECKOUT.GET_GUEST_ID,
				address: ACCOUNT_CHECKOUT.GET_DELIVERY_ADDRESS,
				serviceType: ACCOUNT_CHECKOUT.GET_SERVICE_TYPE,
				orderTime: ACCOUNT_CHECKOUT.GET_ORDER_TIME,
				allMenuItems: MENU_ITEMS.GET_ALL,
				appLoaded: APP_STORE.LOADED,
				loggedIn: ACCOUNT.GET_LOGGED_IN,
			}),
			trackerName() {
				const trackerSettings = this.$appConstants.tracker;
				const name = trackerSettings.name;
				return name;
			},
			displayStorePhone() {
				const phone = this.storePhone;
				if (!phone) {
					this.hasPhone = false;
					return;
				}
				return phone;
			},
			getTotals() {
				const order = this.getOrder;
				return order.totals;
			},
			useVerb() {
				const isDelivery = this.isOrderDelivery;
				return (isDelivery) ? 'to' : 'at';
			},
			getMapHeight() {
				return (this.fromCheckout) ? '182px' : '212px';
			}
		},
		methods: {
			displayPromiseTime() {
				const order = this.getOrder;
				if (!order) {
					return;
				}
				const time = order.promiseTime;
				if (!time) {
					return;
				}
				return time.calendar();
			},
			displayServiceTypeUsed() {
				const serviceType = this.getServiceUsed;
				return this.resolveServiceName(serviceType);
			},
			handleClickReceiptDetails() {
				this.showRecieptDetails = !this.showRecieptDetails;
			},
			orderConfirmationActions() {
				const showReceipt = this.showRecieptDetails;
				const accessoryName = (showReceipt) ? 'caretDown' : 'caretRight';
				return [
					{
						name: 'Receipt Details',
						value: '',
						accessory: accessoryName,
						clickHandler: this.handleClickReceiptDetails,
					},
				];
			},
			handleSignUpLink() {
				this.skipGuard = true;
				this._setCompletedRoute('home');
				this._navigateToSignUp();
			},
			resolveMapDirections() {
				const isDelivery = this.isOrderDelivery;
				const order = this.getOrder;
				if (isDelivery) {
					let directions = {
						origin: this.orderMap.origin,
						destination: order.getDeliveryAddress(),
					};
					return directions;
				}
				return null;
			},
			showMap() {
				return this.appLoaded && (this.orderMap.origin !== null);
			},
			setupNavigationHeader() {
				// TODO use order confirmation route
				// const appConstants = this.$appConstants;
				// const header = appConstants.header;
				this._setHeaderItems([
					'empty',
					'logo',
					'empty'
				]);
			},
		},
		mounted() {
			this.setTrackerActive(true);
			const order = this.getOrder;
			if (!order) {
				let loader = this.$loading.show({
					loader: 'bars',
					canCancel: true,
					onCancel: this.onCancel,
				});
				const vue = this;
				this.fetchOrderGate((success) => {
					if (success) {
						loader.hide();
						return;
					}
					vue.skipGuard = true;
					vue._navigateToGreeting();
				});
			}
		},
		beforeRouteEnter(to, from, next) {
			next((vue) => {
				vue.setupNavigationHeader();
			});
		},
		beforeRouteLeave(to, from, next) {
			if (this.loggedIn || this.skipGuard) {
				next();
				return;
			}
			const leaveOrder = () => {
				this.$modal.hide('warningModal');
				next();
			}
			const stay = () => {
				this.$modal.hide('warningModal');
			}
			const signUp = () => {
				this.$modal.hide('warningModal');
				next();
				this._setCompletedRoute('home');
				this._navigateToSignUp();
				return;
			};
			this.$modal.show(ContentModal, {
				contentComponent: WarningModal,
				contentProperties: {
					buttons: [
						{
							title: 'Sign Up',
							action: signUp,
						},
						{
							title: 'Stay Here',
							action: stay,
						},
						{
							title: 'Leave Tracker',
							action: leaveOrder,
						},
					],
					message: "As a guest user, once you leave this page, you won't be able to track your order.",
				},
				header: {
					showHeader: false,
				},
				footer: {
				showFooter: false,
				},
			}, {
				name: 'warningModal',
				width: '90%',
				height: '360px',
				scrollable: false,
				clickToClose: true,
				delay: 200,
			});
		},
	}
</script>

<style lang="scss" scoped>
	@import 'commonStyle/order-confirmation';
	.map-wrapper {
		min-height: 182px;
		background-color: lightblue;
	}
	.back-to-store {
		padding: 40px 0;
	}
	.order-confirmation-container {
		position: relative;
		background-color: $landingPageBackground;
		.order-tracker-container {
			&.is-minimal {
				border-bottom: none;
			}
		}
	}
	.tracker-header {
		color: $storeButtonColor;
		font-size: 20px;
	}
	.margin-80 {
		margin: auto;
		max-width: 80%;
	}
	.order-items {
		padding-top: 10px;
	}
	.reciept-background {
		background-color: $greyBackground;
		div {
			background-color: $greyBackground;
		}
	}
</style>


// WEBPACK FOOTER //
// src/components/order-confirmation/OrderConfirmationMobile.vue